import { useRef } from 'react';
import { CheckCircle, Edit, X, XCircle } from 'lucide-react';
import type { MapaMesa, Reserva } from '../../types';
import { formatHora } from '../../utils/mesaEstado';

interface ReservaMultiModalProps {
  mesa: MapaMesa;
  onClose: () => void;
  onConfirmar: (r: Reserva) => void;
  onCancelar: (r: Reserva) => void;
  onEditar: (r: Reserva) => void;
}

export function ReservaMultiModal({ mesa, onClose, onConfirmar, onCancelar, onEditar }: ReservaMultiModalProps) {
  const list = mesa.reservasAtivas ?? (mesa.reservaAtiva ? [mesa.reservaAtiva] : []);
  // Só fecha com clique "verdadeiro" na overlay (mousedown+mouseup fora),
  // para não fechar ao arrastar para fora a partir do conteúdo.
  const overlayRef = useRef<HTMLDivElement>(null);
  const pressStartedOnOverlay = useRef(false);
  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={(e) => {
        pressStartedOnOverlay.current = e.target === overlayRef.current;
      }}
      onMouseUp={(e) => {
        if (e.target !== overlayRef.current) pressStartedOnOverlay.current = false;
      }}
      onClick={(e) => {
        if (e.target === overlayRef.current && pressStartedOnOverlay.current) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="multi-title"
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2 id="multi-title" className="modal-title">
            Mesa {mesa.numero} — {list.length} reserva(s)
          </h2>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="modal-body">
          {list.map((r) => (
            <div key={r.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <strong>
                  {formatHora(r.dataDaReserva)} • {r.numeroPessoas} pax — {r.nomeCliente || `Cliente #${r.clienteId}`}
                </strong>
                <span
                  className={`badge ${r.reservaCancelada ? 'badge-danger' : r.reservaFechada ? 'badge-success' : 'badge-warning'}`}
                >
                  {r.reservaCancelada ? 'Cancelada' : r.reservaFechada ? 'Confirmada' : 'Pendente'}
                </span>
              </div>
              {r.contatoReserva && (
                <div className="text-sm" style={{ color: 'var(--c-text-muted)', marginBottom: 8 }}>
                  {r.contatoReserva} {r.notasReserva ? `· ${r.notasReserva}` : ''}
                </div>
              )}
              {(r.criadoPorNome || r.editadoPorNome) && (
                <div className="text-sm" style={{ color: 'var(--c-text-muted)', marginBottom: 8 }}>
                  {r.criadoPorNome && <span>Criado por: {r.criadoPorNome}</span>}
                  {r.editadoPorNome && <span style={{ marginLeft: 8 }}>Editado por: {r.editadoPorNome}</span>}
                </div>
              )}
              {!r.reservaFechada && !r.reservaCancelada && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => onEditar(r)}>
                    <Edit className="w-4 h-4" />
                    Editar
                  </button>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => {
                      onConfirmar(r);
                      onClose();
                    }}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Confirmar
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => {
                      onCancelar(r);
                      onClose();
                    }}
                  >
                    <XCircle className="w-4 h-4" />
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        <footer className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}
